import { Button, ButtonSizesEnum, ButtonThemesEnum } from '@propertyfinder/button';
import { IconSpriteHeart, IconSpriteHeartFilled, IconSpriteHeartThick } from '@propertyfinder/icons';
import { BrowserStorageSessionService } from 'pf-frontend-common/src/service/browser-storage-session/service';
import { Fragment, FunctionalComponent, h } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';

import { domClassMerge } from 'common/helper/dom/class-merge';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { CompleteProfileAsyncComponent } from 'common/module/complete-profile/async.component';
import { MyAccountToggleSaveButtonInteractionSourceEnum } from 'common/module/my-account/toggle-save-button/interaction-source.enum';
import { MyAccountToggleSaveButtonTemplatePropsInterface } from 'common/module/my-account/toggle-save-button/template-props.interface';
import { SAVE_PROPERTY_CONFIRMATION_STORAGE_KEY } from 'common/module/save-property/confirmation-storage-key.constant';
import { SAVE_PROPERTY_RECONFIRMATION_HOURS } from 'common/module/save-property/reconfirmation-hours.constant';
import { StatsTealiumDataLayerEventReferenceEnum } from 'common/module/stats/tealium/data-layer/event-reference.enum';
import { UserAuthAsyncComponent } from 'common/module/user/auth/async.component';

const browserStorageSessionService = BrowserStorageSessionService();

export const MyAccountToggleSaveButtonTemplate: FunctionalComponent<MyAccountToggleSaveButtonTemplatePropsInterface> = (
  props
) => {
  const FavoriteIcon = props.isSmartad ? IconSpriteHeartThick : props.isSaved ? IconSpriteHeartFilled : IconSpriteHeart;

  const body: h.JSX.Element = (
    <Fragment>
      <FavoriteIcon />
    </Fragment>
  );

  const [modalVisible, setModalVisible] = useState(false);
  const [displayCompleteProfile, setDisplayCompleteProfile] = useState(false);
  const [displayUpdateEmailScreen, setDisplayUpdateEmailScreen] = useState(false);
  const [isFirstSavedProperty, setIsFirstSavedProperty] = useState(false);

  const onSuccess = useCallback((): void => {
    setModalVisible(false);

    if (!props.userHasEmail) {
      browserStorageSessionService.setData(SAVE_PROPERTY_CONFIRMATION_STORAGE_KEY, Date.now());
      setIsFirstSavedProperty(true);
      setDisplayCompleteProfile(true);
    } else if (props.isSaved) {
      props.onClickUnsave(props.property, MyAccountToggleSaveButtonInteractionSourceEnum.CARD);
    } else {
      props.onClickSave(props.property, MyAccountToggleSaveButtonInteractionSourceEnum.CARD);
    }
  }, [props.property, props.isSaved, props.userHasEmail]);

  const onClose = (): void => {
    setModalVisible(false);
    props.onCloseAfterSavePopup();
  };

  useEffect(() => {
    if (props.isAfterSavePopupVisible) {
      setModalVisible(true);
    }
  }, [props.isAfterSavePopupVisible]);

  const onSavePropertyClicked = useCallback(
    (e: MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      if (props.isSaved) {
        props.onClickUnsave(props.property, MyAccountToggleSaveButtonInteractionSourceEnum.CARD);
      } else if (!props.isSaved) {
        if (props.displayCompleteProfileScreen) {
          browserStorageSessionService.setData(SAVE_PROPERTY_CONFIRMATION_STORAGE_KEY, Date.now());
          props.sendTealiumEvent('open');
          setIsFirstSavedProperty(true);
          setDisplayCompleteProfile(true);
        } else if (props.displayAlternativeCompleteProfileScreen) {
          setIsFirstSavedProperty(false);

          const timestamp = browserStorageSessionService.getData(SAVE_PROPERTY_CONFIRMATION_STORAGE_KEY) || 0;
          const timeLapse = (Date.now() - Number(timestamp)) / (1000 * 60 * 60);
          if (timeLapse > SAVE_PROPERTY_RECONFIRMATION_HOURS) {
            browserStorageSessionService.setData(SAVE_PROPERTY_CONFIRMATION_STORAGE_KEY, Date.now());
            props.sendTealiumEvent('open');
            setDisplayCompleteProfile(true);
          } else {
            props.onClickSave(props.property, MyAccountToggleSaveButtonInteractionSourceEnum.CARD);
          }
        } else {
          props.onClickSave(props.property, MyAccountToggleSaveButtonInteractionSourceEnum.CARD);
        }
      }
    },
    [props.isSaved, props.property, props.displayCompleteProfileScreen, props.displayAlternativeCompleteProfileScreen]
  );

  const onCompleteProfileModalClose = useCallback(() => {
    setDisplayCompleteProfile(false);
    props.onClickSave(props.property, MyAccountToggleSaveButtonInteractionSourceEnum.CARD);
  }, [props.property]);

  const onUpdateEmailModalClose = useCallback(() => {
    setDisplayUpdateEmailScreen(false);
    props.onClickSave(props.property, MyAccountToggleSaveButtonInteractionSourceEnum.CARD);
  }, [props.property]);

  return (
    <Fragment>
      {props.isSmartad ? (
        <button
          data-qs='cta'
          onClick={(e): void => onSavePropertyClicked(e)}
          className={domClassMerge('card__button', 'card__button--smart-ad', props.className, {
            'card__button--filled': props.isSaved,
          })}
        >
          {body}
        </button>
      ) : (
        <Button
          className={domClassMerge(props.className, { 'card__button--filled': props.isSaved })}
          size={ButtonSizesEnum.SMALL}
          theme={ButtonThemesEnum.SECONDARY}
          onClick={(e: MouseEvent): void => onSavePropertyClicked(e)}
          htmlAttributes={{
            'data-qs': 'cta',
          }}
          icon={{
            component: FavoriteIcon,
            position: 'prefix',
          }}
        />
      )}

      {modalVisible ? (
        <UserAuthAsyncComponent
          reference={StatsTealiumDataLayerEventReferenceEnum.SAVED_PROPERTIES}
          customModalTitle={'login-modal-save-property-title'}
          customModalDescription={'login-modal-save-property-vp'}
          customModalImage={__('assets/common/save-property.svg')}
          onSuccess={onSuccess}
          onClose={onClose}
        />
      ) : null}

      {displayCompleteProfile && (
        <Fragment>
          <CompleteProfileAsyncComponent
            displayEmailInput={false}
            customModalTitle={i18nTranslate('SavedProperty_UserUpdateModalTitle')}
            customModalDescription={
              isFirstSavedProperty
                ? i18nTranslate('SavedProperty_ConfirmationModalDescFirstSaved')
                : i18nTranslate('SavedProperty_ConfirmationModalDesc')
            }
            customPrimaryButtonLabel={i18nTranslate('SavedProperty_ConfirmationModalDescPrimaryAction')}
            onSuccess={(): void => {
              props.sendTealiumEvent('continue');
              setDisplayCompleteProfile(false);
              setDisplayUpdateEmailScreen(true);
            }}
            onCancel={(): void => {
              props.sendTealiumEvent('skip');
              onCompleteProfileModalClose();
            }}
            onClose={onCompleteProfileModalClose}
          />
        </Fragment>
      )}

      {displayUpdateEmailScreen && (
        <CompleteProfileAsyncComponent
          displaySecondaryButton={false}
          customModalTitle={i18nTranslate('SavedProperty_UserUpdateModalTitle')}
          customModalDescription={i18nTranslate('SavedProperty_UserUpdateModalDesc')}
          onSuccess={(): void => {
            props.sendTealiumEvent('submit');
            onUpdateEmailModalClose();
          }}
          onCancel={onUpdateEmailModalClose}
          onClose={onUpdateEmailModalClose}
        />
      )}
    </Fragment>
  );
};
